import React from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import type { CostDataSummary, EstimateCapabilities, EstimateJob, EstimateRevision, PublicPriceEvidencePublic } from '../api/constructionPriceEstimatorFunctions';
import type { ApproximateCostPlanPublic } from '../api/approximateCostPlan';
import ApproximateCostPlan, { hasApproximateCostPlanPrices } from './ApproximateCostPlan';
import type { EstimateDraft, PriceSearchPrefill } from '../api/constructionPriceEstimatorFunctions';
import { UnifiedResearchProgress } from './ConstructionPriceResearch';
import { SavedWorkActionRow } from '../components/SavedWorkActionRow';

const acknowledgementLabel = (key: string): string => key.replace(/_/g, ' ');

export const ConstructionPriceCalculateControls: React.FC<{
  visible: boolean;
  canEdit: boolean;
  busy: boolean;
  preparingPrefill: boolean;
  useSavedSourceResults: boolean;
  setUseSavedSourceResults: React.Dispatch<React.SetStateAction<boolean>>;
  primaryCostDataConfigured: boolean;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  onCalculate: () => void;
  disabled?: boolean;
  aiPlanningEstimateCount?: number;
  rerunReminder?: string | null;
}> = ({ canEdit, busy, preparingPrefill, useSavedSourceResults,
  setUseSavedSourceResults, primaryCostDataConfigured, saveStatus, onCalculate,
  disabled = false, aiPlanningEstimateCount = 0, rerunReminder }) => {
  return <Stack data-tour-id="price-calculate" component="section" aria-label="Price calculation" alignItems="center" spacing={0} sx={{ mb: 0.5, '& .MuiFormControlLabel-root': { m: 0, minHeight: 44 } }}>
    {aiPlanningEstimateCount > 0 && <Alert severity="info" sx={{ maxWidth: 620, textAlign: 'left', mb: 0.5 }}>
      This rough estimate includes editable AI planning assumptions. They are not verified source facts, supplier quotes, or Wage Determination rates.
    </Alert>}
    {rerunReminder && <Alert severity="error" role="alert" aria-label="Estimate needs a rerun" sx={{ maxWidth: 620, textAlign: 'left', mb: 0.5 }}>
      <Typography fontWeight={700}>Estimate needs a rerun</Typography>{rerunReminder}
    </Alert>}
    <SavedWorkActionRow
      action={
        <Button variant="contained" disabled={!canEdit || busy || preparingPrefill || disabled}
        onClick={onCalculate}>{busy ? 'Calculating…' : 'Calculate prices'}</Button>
      }
      savedWork={<FormControlLabel control={<Checkbox inputProps={{ id: 'use-saved-source-results', name: 'use-saved-source-results' }} checked={useSavedSourceResults} disabled={!canEdit || busy || preparingPrefill} onChange={(event) => setUseSavedSourceResults(event.target.checked)} />} label="Use saved work" />}
    />
    <Typography variant="caption" color="text.secondary" textAlign="center">
      {primaryCostDataConfigured
        ? 'Checked uses a current saved calculation. Uncheck to rerun server-owned selection against approved snapshots.'
        : 'Checked uses matching results that are still current. Uncheck to request a fresh bounded search.'}
    </Typography>
    <Box role="status" aria-live="polite" aria-label="Project facts save status" sx={{ mt: 1 }}>
      {saveStatus === 'saving' && <Typography variant="caption">Saving project facts…</Typography>}
      {saveStatus === 'saved' && <Typography variant="caption">Project facts saved.</Typography>}
      {saveStatus === 'error' && <Typography variant="caption" color="error">Project facts were not saved.</Typography>}
    </Box>
  </Stack>;
};

interface ConstructionPriceResultsProps {
  timingContext?: { draft: Pick<EstimateDraft, 'timing_requirements'>;
    prefill: Pick<PriceSearchPrefill, 'timing_source_status' | 'status'> | null };
  sourceSearchBusy: boolean;
  sourceSearchFailed: boolean;
  resultLoadUnavailable: boolean;
  sourceSearchCancelled: boolean;
  calculationStage: 'saving' | 'searching' | 'preparing' | 'reconnecting';
  job: EstimateJob | null;
  showCancel: boolean;
  canEdit: boolean;
  canDownload: boolean;
  cancelling: boolean;
  cancelCalculation: () => void;
  error: string | null;
  showPrefillEntry: boolean;
  hasCurrentResult: boolean;
  legacyManualOnly: boolean;
  primaryApproximateCostPlan: ApproximateCostPlanPublic;
  publicPriceEvidence?: PublicPriceEvidencePublic | null;
  wageRefreshFailed: boolean;
  awardEvidenceIsContextOnly: boolean;
  previousAwardsPanel: React.ReactNode;
  wageDeterminationEvidence: React.ReactNode;
  sourcesChecked: Array<{ label: string; detail?: string }>;
  awardSourceRetryMessage: string | null;
  supportingEvidence: Array<{ id: string; label: string; content: React.ReactNode }>;
  displayedCostDataSummary: CostDataSummary | null;
  revision: EstimateRevision | null;
  requiredAcknowledgements: string[];
  acknowledgedKeys: Set<string>;
  setAcknowledgedKeys: React.Dispatch<React.SetStateAction<Set<string>>>;
  romAckRequired: boolean;
  romAcknowledged: boolean;
  setRomAcknowledged: React.Dispatch<React.SetStateAction<boolean>>;
  isRom: boolean;
  romFinalizationAllowed: boolean;
  finalizing: boolean;
  downloadEstimate: () => void;
  capabilities: EstimateCapabilities;
}

export const hasConstructionPriceResultPrices = (
  plan: ApproximateCostPlanPublic,
  awardEvidenceIsContextOnly: boolean,
  isRom: boolean,
  revision: EstimateRevision | null,
): boolean => hasApproximateCostPlanPrices(plan, awardEvidenceIsContextOnly)
  || Boolean(!isRom && revision?.work_items?.some((item) => (
    item.cost_data?.pricing_kind !== 'unresolved'
    && ['low', 'base', 'high'].some((scenario) => {
      const amount = item[scenario] ?? item.total?.[scenario];
      return amount != null && amount !== '' && Number.isFinite(Number(amount));
    })
  )));

export const ConstructionPriceResults: React.FC<ConstructionPriceResultsProps> = ({
  sourceSearchBusy, sourceSearchFailed, resultLoadUnavailable, sourceSearchCancelled, calculationStage,
  job, showCancel, canEdit, canDownload, cancelling, cancelCalculation, error,
  showPrefillEntry, hasCurrentResult, legacyManualOnly,
  primaryApproximateCostPlan, publicPriceEvidence, wageRefreshFailed,
  awardEvidenceIsContextOnly, previousAwardsPanel, wageDeterminationEvidence,
  sourcesChecked, awardSourceRetryMessage, supportingEvidence,
  displayedCostDataSummary, revision, requiredAcknowledgements,
  acknowledgedKeys, setAcknowledgedKeys, romAckRequired, romAcknowledged,
  setRomAcknowledged, isRom, romFinalizationAllowed, finalizing,
  downloadEstimate, capabilities, timingContext,
}) => {
  const priceTableHasResults = hasConstructionPriceResultPrices(
    primaryApproximateCostPlan, awardEvidenceIsContextOnly, isRom, revision,
  );
  return <>
      {sourceSearchBusy && (
        <Paper
          component="section"
          variant="outlined"
          sx={{ p: { xs: 2.5, md: 4 }, mb: 2, minHeight: 180, display: 'flex', alignItems: 'center' }}
          role="status"
          aria-label="Calculating prices"
          aria-live="polite"
          aria-busy="true"
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ width: '100%' }}>
            <CircularProgress size={38} aria-label="Calculating prices" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" component="h2">Calculating prices</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                {calculationStage === 'saving' && 'Saving project details…'}
                {calculationStage === 'searching' && 'Checking approved price evidence and research context…'}
                {calculationStage === 'preparing' && 'Preparing price results…'}
                {calculationStage === 'reconnecting' && 'Still calculating; reconnecting…'}
              </Typography>
              {job?.unified_source_research?.lanes && <UnifiedResearchProgress lanes={job.unified_source_research.lanes} />}
            </Box>
            {showCancel && (
              <Button color="error" variant="outlined" disabled={!canEdit || cancelling} onClick={() => void cancelCalculation()}>
                {cancelling ? 'Cancelling…' : 'Cancel calculation'}
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {!sourceSearchBusy && sourceSearchFailed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography fontWeight={800}>{resultLoadUnavailable
            ? 'Calculation completed, but its results could not be loaded.'
            : 'Price search could not be completed. No new prices were loaded.'}</Typography>
          {error && <Typography variant="body2" sx={{ mt: 0.5 }}>{error}</Typography>}
          <Typography variant="body2" sx={{ mt: 0.5 }}>Select Calculate prices to try again.</Typography>
        </Alert>
      )}

      {!sourceSearchBusy && sourceSearchCancelled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography fontWeight={800}>Calculation cancelled. Your saved draft is unchanged.</Typography>
          <Typography variant="body2">No new prices were loaded. Select Calculate prices when you are ready to run the search again.</Typography>
        </Alert>
      )}

      {!showPrefillEntry && !sourceSearchBusy && !sourceSearchFailed && !sourceSearchCancelled && !hasCurrentResult && (
        <Paper component="section" variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="h6" component="h2">No price search has been run yet.</Typography>
          <Typography variant="body2" color="text.secondary">Confirm the project details, then select Calculate prices.</Typography>
        </Paper>
      )}

      {!sourceSearchBusy && !sourceSearchFailed && !sourceSearchCancelled && legacyManualOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography fontWeight={800}>Saved legacy manual estimate</Typography>
          <Typography variant="body2">No external sources were used; this revision contains only user-entered manual allowances.</Typography>
        </Alert>
      )}

      {!sourceSearchBusy && !sourceSearchFailed && !sourceSearchCancelled && hasCurrentResult && wageRefreshFailed && (
        <Alert severity="info" sx={{ mb: 2 }} aria-label="Saved Wage Determination reused">
          <Typography fontWeight={800}>Saved Wage Determination reused</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            A previously verified Wage Determination still matches these contract details, so its labor rates were used after the latest lookup did not find a replacement.
          </Typography>
        </Alert>
      )}

      {!sourceSearchBusy && !sourceSearchFailed && !sourceSearchCancelled && hasCurrentResult && (
        <Box data-tour-id="price-rom" sx={{ position: 'relative' }}>
        <span data-tour-id="price-rom-anchor" aria-hidden="true" style={{ position: 'absolute', top: 0, left: '50%', width: 1, height: 1 }} />
        <ApproximateCostPlan
          plan={primaryApproximateCostPlan}
          timingRequirements={timingContext?.draft.timing_requirements}
          timingSourceStatus={timingContext?.prefill?.timing_source_status}
          timingIsStale={timingContext?.prefill?.status === 'stale' || (!timingContext?.prefill
            && Boolean(timingContext?.draft.timing_requirements?.some(item => item.source_id.trim() && item.evidence_quote.trim())))}
          awardEvidence={previousAwardsPanel}
          wageDeterminationEvidence={wageDeterminationEvidence}
          publicPriceEvidence={publicPriceEvidence}
          sourcesChecked={sourcesChecked}
          wageRefreshFailed={wageRefreshFailed}
          awardReferenceUnavailableReason={awardSourceRetryMessage ?? undefined}
          awardEvidenceIsContextOnly={awardEvidenceIsContextOnly}
          supportingEvidence={supportingEvidence}
        />
        </Box>
      )}

      {hasCurrentResult && priceTableHasResults && !sourceSearchBusy && !sourceSearchFailed && !sourceSearchCancelled && (
        <Box component="footer" aria-label="Estimate result actions" sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 2, mb: 2 }}>
          {requiredAcknowledgements.map((key) => {
            const id = `acknowledgement-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
            return <FormControlLabel key={key} control={<Checkbox inputProps={{ id, name: id }} checked={acknowledgedKeys.has(key)} onChange={(event) => setAcknowledgedKeys((current) => {
              const next = new Set(current);
              if (event.target.checked) next.add(key);
              else next.delete(key);
              return next;
            })} />} label={`I acknowledge: ${acknowledgementLabel(key)}.`} />;
          })}
          {romAckRequired && <FormControlLabel control={<Checkbox inputProps={{ id: 'acknowledgement-comparable-rom', name: 'acknowledgement-comparable-rom' }} checked={romAcknowledged} onChange={(event) => setRomAcknowledged(event.target.checked)} />} label="I acknowledge the comparable-only ROM limitations and that this is not an itemized estimate or IGE." />}
          {isRom && !romFinalizationAllowed && <Alert severity="info" sx={{ mt: 1 }}>Comparable-only ROM finalization is disabled by current server policy. You may review this planning range, but cannot finalize it.</Alert>}
          <Stack data-tour-id="price-download" direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
            <Button variant="contained" startIcon={<DownloadIcon />} disabled={!canDownload || finalizing}
              onClick={downloadEstimate}>{finalizing ? (canEdit ? 'Saving and downloading…' : 'Downloading…') : 'Download estimate'}</Button>
          </Stack>
        </Box>
      )}

</>;
};
